import React, { useCallback, useMemo } from "react";
import { useIntl } from "react-intl";
import { Box, Typography } from "@mui/material";
import GeoMap from "bundles/common/components/GeoMap";

const offerPoints = (offer) => (offer?.locations || [])
  .filter((loc) => loc?.lat != null && loc?.lng != null)
  .map((loc) => ({ id: offer.id, lat: Number(loc.lat), lng: Number(loc.lng), data: offer }));

/**
 * Candidate job map: plots each offer at every location that has server-side
 * coordinates, on top of the shared GeoMap.
 */
const JobsMap = ({ jobOffers = [], selectedJobId, onSelectJob }) => {
  const intl = useIntl();

  const points = useMemo(() => jobOffers.flatMap(offerPoints), [jobOffers]);

  const focus = useMemo(() => {
    const offer = jobOffers.find((o) => o.id === selectedJobId);
    return offerPoints(offer)[0] || null;
  }, [jobOffers, selectedJobId]);

  const getLabel = useCallback((items) => (
    items.length === 1
      ? (items[0].data.job_title
        || items[0].data.company?.name
        || intl.formatMessage({ id: "candidate.jobs_tab.map.job_fallback" }))
      : intl.formatMessage({ id: "candidate.jobs_tab.map.offers_count" }, { count: items.length })
  ), [intl]);

  const renderItems = useCallback(({ items, close }) => items.map(({ data: offer }) => (
    <Box
      key={offer.id}
      onClick={() => {
        close();
        onSelectJob?.(offer);
      }}
      sx={{
        display: "flex", alignItems: "center", gap: "10px",
        padding: "10px 6px", borderRadius: "8px", cursor: "pointer",
        "&:hover": { bgcolor: "hsla(160, 10%, 96%, 1)" },
      }}
    >
      {(offer.company?.small_logo_url || offer.company?.logo_url) && (
        <Box
          component="img"
          src={offer.company.small_logo_url || offer.company.logo_url}
          alt=""
          sx={{
            width: "36px", height: "36px", borderRadius: "50%",
            objectFit: "cover", flexShrink: 0, border: "1px solid hsla(160, 10%, 90%, 0.6)",
          }}
        />
      )}
      <Box sx={{ overflow: "hidden" }}>
        <Typography sx={{
          fontSize: "13px", color: "hsl(178, 93%, 10%)",
          whiteSpace: "nowrap", textOverflow: "ellipsis", overflow: "hidden",
          fontFamily: "Segoe UI Bold, sans-serif, system-ui",
        }}>
          {offer.job_title || intl.formatMessage({ id: "candidate.jobs_tab.map.job_fallback" })}
        </Typography>
        <Typography sx={{
          fontSize: "11px", color: "hsl(178, 10%, 45%)",
          whiteSpace: "nowrap", textOverflow: "ellipsis", overflow: "hidden",
          fontFamily: "Segoe UI Regular, sans-serif, system-ui",
        }}>
          {offer.company?.name || ""}
        </Typography>
      </Box>
    </Box>
  )), [intl, onSelectJob]);

  return (
    <GeoMap
      points={points}
      getLabel={getLabel}
      renderItems={renderItems}
      focus={focus}
      fullscreenLabel={intl.formatMessage({ id: "map_fullscreen", defaultMessage: "Full screen" })}
      exitFullscreenLabel={intl.formatMessage({ id: "map_exit_fullscreen", defaultMessage: "Exit full screen" })}
      language={intl.locale}
      errorMessage={intl.formatMessage({ id: "candidate.jobs_tab.map.unavailable" })}
    />
  );
};

export default JobsMap;
